
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { addApolloState, CourseDetail_InventoryCourseFragment, initializeApollo, useGetCourseDetailPageByIdQuery, } from 'content-service';
import { alertBannersQuery, courseDetailPageQuery, footerQuery, swnzHeaderQuery, swnzSiteSearchQuery, swnzTopNavQuery, } from 'content-service/src/helpers';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import AlertBanners from 'src/components/AlertBanner/AlertBanners';
import { CONTENT_SERVICE } from 'src/constants';
import { createInventoryCourseUtagView, useTrackInventoryViews } from 'tracking';
import { DetailPageSeo, Error } from 'ui';
import { getLocalisedPath, getQueryLocale, REVALIDATE } from 'utils';
import { createCanonicalUrl } from 'utils/src/helpers';
import { ROUTES, SITE_CONFIG } from '../../../constants';
import CourseDetailPage from '../../../templates/CourseDetailPage';
interface CourseDetailProps {
    courseId: string;
    preview: boolean;
    locale: string;
}
const CourseDetail = ({ courseId, preview, locale }: CourseDetailProps) => {
    const { data, error } = useGetCourseDetailPageByIdQuery({ variables: { courseId, preview } });
    const { courseDetail, disclaimer, detailBanner } = data || {};
    const { locales = [], defaultLocale = '' } = useRouter();
    useTrackInventoryViews(createInventoryCourseUtagView(courseDetail as CourseDetail_InventoryCourseFragment));
    if (error || courseDetail?.__typename !== 'inventoryCourse') {
        return <Error preview={preview} statusCode={404}/>;
    }
    const asPath = `${ROUTES.COURSE.DETAIL}/${courseId}`;
    const canonicalUrl = createCanonicalUrl(asPath, [...locales], defaultLocale);
    const pageUrl = getLocalisedPath(process.env.NEXT_PUBLIC_URL ?? '', asPath, locale, defaultLocale);
    const sharingUrl = encodeURI(getLocalisedPath(SITE_CONFIG.SITE_PROD_BASE_URL, asPath, locale, defaultLocale));
    const pageName = `${courseDetail.name} | ${courseDetail?.provider?.name}`;
    return (<>
      <AlertBanners preview={preview} locale={locale}/>
      <DetailPageSeo pageName={pageName} pageUrl={pageUrl} inputData={courseDetail} canonicalUrl={canonicalUrl}/>
      <CourseDetailPage courseDetail={courseDetail} disclaimer={disclaimer} detailBanner={detailBanner} pageUrl={sharingUrl}/>
    </>);
};
export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: [],
        fallback: 'blocking'
    };
};
const getStaticProps: GetStaticProps = async ({ params, preview = false, locale, defaultLocale, }) => {
    const { courseId = '' } = params || {};
    const queryLocale = getQueryLocale(locale, defaultLocale);
    try {
        const apolloClient = initializeApollo({ preview });
        let courseDetail;
        const [pageQueryResponse, ...rest] = await Promise.allSettled([
            courseDetailPageQuery({ apolloClient, preview, id: courseId }),
            swnzHeaderQuery({
                apolloClient,
                preview,
                codeId: CONTENT_SERVICE.HEADER_CODE_ID,
                locale: queryLocale
            }),
            swnzTopNavQuery({
                apolloClient,
                codeId: CONTENT_SERVICE.SECONDARY_TOP_NAV,
                preview,
                locale: queryLocale
            }),
            swnzTopNavQuery({
                apolloClient,
                codeId: CONTENT_SERVICE.SECONDARY_TOP_NAV_LOGGED_IN,
                preview,
                locale: queryLocale
            }),
            footerQuery({ apolloClient, preview, locale: queryLocale }),
            swnzSiteSearchQuery({
                apolloClient,
                codeId: CONTENT_SERVICE.QUICK_LINKS,
                preview,
                locale: queryLocale
            }),
            alertBannersQuery({
                apolloClient,
                preview,
                locale: queryLocale
            }),
        ]);
        rest.filter((queryResponse) => {
            if (queryResponse.status !== 'fulfilled')
                console.error(`⚠️  Error in query response in page [courseId] for courseId: ${courseId}`);
        });
        if (pageQueryResponse.status === 'fulfilled') {
            courseDetail = pageQueryResponse.value.data.courseDetail;
        }
        // If no ID returned from query we can be assured that this page does not exist
        if (courseDetail?.__typename === 'inventoryCourse' && !courseDetail.id)
            return { notFound: true };
        if (!courseDetail?.active) {
            return { notFound: true };
        }
        return addApolloState(apolloClient, {
            props: { courseId, preview, locale: queryLocale },
            revalidate: REVALIDATE.FINDER.DETAILS
        });
    }
    catch (error) {
        return { notFound: true };
    }
};
export default CourseDetail;

    async function __Next_Translate__getStaticProps__197a40bdfac__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/study-options/course/[courseId]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__197a40bdfac__ as getStaticProps }
  